import { useState } from 'preact/hooks';
import { AlertTriangle, Check } from 'lucide-preact';
import clsx from 'clsx';

/**
 * Traveller avatar with an integrated status badge in the bottom-right
 * corner — same pattern as online indicators in chat apps. The badge
 * carries the connection / route state so the watcher sees both "who"
 * and "is the data trustworthy" in one visual unit, no separate pill
 * floating elsewhere.
 *
 * `status` values map to:
 *   - `live`        – fresh socket connection, recent waypoint (green, pulse)
 *   - `stale`       – 60s+ without a waypoint (amber, static)
 *   - `offline`     – socket dropped or 5min+ silence (red, static)
 *   - `connecting`  – boot / reconnect (grey, pulse)
 *   - `arrived`     – route.finished (green check)
 *   - `emergency`   – route.emergency.state === 'active' (red alert)
 *   - `null`        – no badge (route loading, etc.)
 */
export type AvatarStatus = 'live' | 'stale' | 'offline' | 'connecting' | 'arrived' | 'emergency' | null;

type AvatarProps = {
  photoUrl?: string | null | undefined;
  name?: string | null | undefined;
  size?: number;
  status?: AvatarStatus;
  class?: string;
};

export function Avatar({
  photoUrl,
  name,
  size = 40,
  status = null,
  class: extraClass,
}: AvatarProps) {
  const [errored, setErrored] = useState(false);
  const initial = (name?.trim()?.[0] ?? '?').toUpperCase();
  const hasPhoto = !!photoUrl && !errored;
  const style = { width: `${size}px`, height: `${size}px`, fontSize: `${Math.round(size * 0.42)}px` };

  return (
    <span class={clsx('relative inline-flex shrink-0', extraClass)} style={style}>
      <span
        class="inline-flex h-full w-full items-center justify-center rounded-full border-2 border-surface bg-primary text-white font-semibold shadow-sm overflow-hidden"
        aria-hidden="true"
      >
        {hasPhoto ? (
          <img
            src={photoUrl ?? ''}
            alt=""
            class="h-full w-full object-cover"
            onError={() => setErrored(true)}
          />
        ) : (
          <span>{initial}</span>
        )}
      </span>
      {status && <StatusBadge status={status} parentSize={size} />}
    </span>
  );
}

function StatusBadge({ status, parentSize }: { status: NonNullable<AvatarStatus>; parentSize: number }) {
  // Badge is ~40% of the avatar size — large enough to read, small enough
  // not to compete visually.
  const badgeSize = Math.max(14, Math.round(parentSize * 0.42));
  const iconSize = Math.max(10, Math.round(badgeSize * 0.65));
  return (
    <span
      class={clsx(
        'absolute -right-0.5 -bottom-0.5 inline-flex items-center justify-center rounded-full border-2 border-surface shadow-sm',
        BADGE_STYLE[status],
      )}
      style={{ width: `${badgeSize}px`, height: `${badgeSize}px` }}
      role="status"
      aria-label={BADGE_ARIA[status]}
    >
      {status === 'arrived' && <Check size={iconSize} class="text-white" aria-hidden="true" />}
      {status === 'emergency' && <AlertTriangle size={iconSize} class="text-white" aria-hidden="true" />}
      {(status === 'live' || status === 'connecting') && (
        <span class="absolute inset-0 motion-safe:animate-ping rounded-full opacity-60" style={{ backgroundColor: 'currentColor' }} />
      )}
    </span>
  );
}

const BADGE_STYLE: Record<NonNullable<AvatarStatus>, string> = {
  live: 'bg-safety text-safety',
  stale: 'bg-warning text-warning',
  offline: 'bg-emergency text-emergency',
  connecting: 'bg-border text-border',
  arrived: 'bg-safety text-safety',
  emergency: 'bg-emergency text-emergency',
};

const BADGE_ARIA: Record<NonNullable<AvatarStatus>, string> = {
  live: 'Live',
  stale: 'Signal weak',
  offline: 'Offline',
  connecting: 'Connecting',
  arrived: 'Arrived safely',
  emergency: 'Emergency',
};
